import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { COLORS } from '../../commons/Colors'
import ConfirmationModalComponent from '../../commons/components/ConfirmationModalComponent'
import LoadingComponent from '../../commons/components/LoadingComponent'
import RoundButtonComponent from '../../commons/components/RoundButtonComponent'
import SearchInputComponent from '../../commons/components/SearchInputComponent'
import SnackBarComponent from '../../commons/components/SnackBarComponent'
import debounce from '../../commons/utils/Debounce'
import { useUserContext } from '../../context/UserContext'
import { INotification } from '../../models/INotification'
import {
    getNotifications,
    createNotification,
    deleteNotification,
    updateNotification,
} from '../API/NotificationAPI'
import NotificationCardListComponent from '../NotificationCardListComponent'
import NotificationCreationModalComponent from '../NotificationCreationModalComponent'
import NotRegistredComponent from '../NotRegistredComponent'
import { downloadFile } from '../utils/FileUtils'

// TODO - quebrar em screen/container/view
function NotificationContentScreen(): JSX.Element {
    const { user } = useUserContext()
    const [notifications, setNotifications] = useState<INotification[]>([])
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isSending, setIsSending] = useState<boolean>(false)
    const [isNewNotificationModalVisible, setIsNewNotificationModalVisible] =
        useState<boolean>(false)
    const [isUnpublishModalVisible, setIsUnpublishModalVisible] =
        useState<boolean>(false)
    const [selectedNotification, setSelectedNotification] = useState<
        INotification | undefined
    >()
    const [selectedToPublish, setSelectedToPublish] = useState<
        Map<string, boolean>
    >(new Map())
    const [idToDelete, setIdToDelete] = useState<string>()

    const [currentPage, setCurrentPage] = useState<number>(1)
    const [totalPages, setTotalPages] = useState<number>(1)
    const limit = 10

    const [snackBarConfig, setSnackBarConfig] = useState<{
        isVisible: boolean
        message: string
        status: 'success' | 'error' | 'warning' | 'info'
    }>({ message: '', isVisible: false, status: 'success' })

    const fetchNotifications = async (
        currentPage: number,
        searchTerm?: string
    ) => {
        setIsLoading(true)
        const offset = (currentPage - 1) * limit
        const response = await getNotifications(
            user.isDsm ? 'Gdv' : 'Portal',
            limit,
            offset,
            searchTerm
        )
        if (response.data) {
            setNotifications(response.data)
            setTotalPages(Math.ceil(response.total / limit))
        } else {
            setSnackBarConfig({
                message: 'Erro ao buscar notícias!',
                isVisible: true,
                status: 'error',
            })
        }
        setIsLoading(false)
    }

    useEffect(() => {
        if (user) {
            fetchNotifications(currentPage)
        }
    }, [user])

    const handlePageChange = (
        _event: React.ChangeEvent<unknown>,
        page: number
    ) => {
        setCurrentPage(page)
        fetchNotifications(page)
    }

    const reloadData = async () => {
        setCurrentPage(1)
        setSearchTerm('')
        setSelectedToPublish(new Map())
        setSelectedNotification(undefined)
        await fetchNotifications(1)
    }

    const isCardSelected = (notificationId: string): boolean => {
        return !!selectedToPublish.get(notificationId)
    }

    const handleCardSelection = (notificationId: string): void => {
        if (!isCardSelected(notificationId)) {
            setSelectedToPublish((prev) => {
                const newMap = new Map(prev)
                newMap.set(notificationId, true)
                return new Map(newMap)
            })
        } else {
            setSelectedToPublish((prev) => {
                const newMap = new Map(prev)
                newMap.delete(notificationId)
                return new Map(newMap)
            })
        }
    }

    const handleOnEditNotification = (notification: INotification) => {
        setSelectedNotification(notification)
        setIsNewNotificationModalVisible(true)
    }

    const handleOnCloseModal = () => {
        setSelectedNotification(undefined)
        setIsNewNotificationModalVisible(false)
    }

    const handleOnCloseSnackBar = () => {
        setSnackBarConfig({ message: '', isVisible: false, status: 'success' })
    }

    const handleOnSaveNotification = async (
        notification: INotification,
        files?: File[]
    ): Promise<void> => {
        setIsSending(true)
        const result = selectedNotification?.id
            ? await updateNotification(notification, files)
            : await createNotification(notification, files)
        setIsSending(false)

        if (result.data) {
            await reloadData()
            setSnackBarConfig({
                message: `Notícia ${
                    selectedNotification?.id ? 'editada' : 'criada'
                } com sucesso!`,
                isVisible: true,
                status: 'success',
            })
        } else {
            setSnackBarConfig({
                message: 'Erro ao criar Notícia!',
                isVisible: true,
                status: 'error',
            })
        }
    }

    const handleDownloadFile = async (path: string) => {
        try {
            await downloadFile(path)
        } catch (error) {
            console.error('Erro ao baixar a imagem:', error)
            setSnackBarConfig({
                message: 'Erro ao baixar imagem, tente novamente mais tarde!',
                isVisible: true,
                status: 'error',
            })
        }
    }

    const handleOnDeleteNotification = (notificationId: string) => {
        setIdToDelete(notificationId)
    }

    const handleDelete = async (id: string) => {
        setIsSending(true)
        const result = await deleteNotification(id)
        console.log(result)

        if (result.status === 200) {
            await reloadData()
            setSnackBarConfig({
                message: 'Notícia excluída com sucesso!',
                isVisible: true,
                status: 'success',
            })
        } else {
            setSnackBarConfig({
                message: 'Erro ao tentar excluir notícia.',
                isVisible: true,
                status: 'error',
            })
        }
        setIsSending(false)
        setIdToDelete(undefined)
    }

    const handleOnUnpublish = (notification: INotification) => {
        setSelectedNotification(notification)
        setIsUnpublishModalVisible(true)
    }

    const handleUnpublish = async (notification: INotification) => {
        setIsSending(true)
        const result = await updateNotification({
            ...notification,
            published: false,
        } as INotification)

        if (result.data) {
            await reloadData()
            setSnackBarConfig({
                message: 'Notícia inativada com sucesso!',
                isVisible: true,
                status: 'success',
            })
        } else {
            setSnackBarConfig({
                message: 'Erro ao tentar inativar notícia.',
                isVisible: true,
                status: 'error',
            })
        }
        setIsSending(false)
        setIsUnpublishModalVisible(false)
    }

    const handlePublish = async () => {
        setIsSending(true)
        const notificationsToPublish = notifications.filter((notification) =>
            selectedToPublish.has(notification.id!)
        )
        const result = await Promise.all(
            notificationsToPublish.map((notification) =>
                updateNotification({
                    ...notification,
                    published: true,
                } as INotification)
            )
        )
        if (result.every((r) => r.data)) {
            await reloadData()
            setSnackBarConfig({
                message: 'Notícias publicadas com sucesso!',
                isVisible: true,
                status: 'success',
            })
        } else {
            setSnackBarConfig({
                message: 'Erro ao tentar publicar notícias.',
                isVisible: true,
                status: 'error',
            })
        }
        setSelectedToPublish(new Map())
        setIsSending(false)
    }

    const handleSearch = (term: string) => {
        setSearchTerm(term)
        setCurrentPage(1)
        debounce(() => {
            fetchNotifications(1, term)
        })
    }

    if (isLoading)
        return (
            <NotificationTabContainer>
                <LoadingContainer>
                    <LoadingComponent />
                </LoadingContainer>
            </NotificationTabContainer>
        )

    return (
        <NotificationTabContainer>
            <TabContentContainer>
                <TabHeaderContainer>
                    <FilterContainer>
                        <SearchInputComponent
                            label="Filtro"
                            placeholderText="Filtrar por tipo e descrição"
                            onChange={handleSearch}
                            value={searchTerm}
                        />
                    </FilterContainer>

                    <NewNotificationButtonContainer>
                        <RoundButtonComponent
                            text="Publicar"
                            disabled={selectedToPublish.size === 0}
                            onClick={() => handlePublish()}
                            isLoading={isSending}
                        />
                        <RoundButtonComponent
                            text="Nova Notícia"
                            onClick={() =>
                                setIsNewNotificationModalVisible(true)
                            }
                        />
                    </NewNotificationButtonContainer>
                </TabHeaderContainer>
                {notifications.length ? (
                    <CardsContainer>
                        <NotificationCardListComponent
                            notifications={notifications}
                            onEdit={handleOnEditNotification}
                            onDelete={handleOnDeleteNotification}
                            onUnpublish={handleOnUnpublish}
                            isCardSelected={isCardSelected}
                            handleCardSelection={handleCardSelection}
                            handleDownloadFile={handleDownloadFile}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </CardsContainer>
                ) : (
                    <NotRegistredComponent />
                )}
            </TabContentContainer>

            <NotificationCreationModalComponent
                isVisible={isNewNotificationModalVisible}
                selectedNotification={selectedNotification}
                setIsVisible={handleOnCloseModal}
                onSave={handleOnSaveNotification}
                isSending={isSending}
            />

            {snackBarConfig.isVisible && (
                <SnackBarComponent
                    severity={snackBarConfig.status}
                    isVisible={snackBarConfig.isVisible}
                    showTimeInMs={3000}
                    handleClose={handleOnCloseSnackBar}
                    message={snackBarConfig.message}
                />
            )}

            <ConfirmationModalComponent
                isVisible={!!idToDelete}
                message={
                    <>
                        Tem certeza que deseja excluir a publicação selecionada?
                        A publicação será excluída do sistema e, caso esteja
                        publicada, será apagada do aplicativo / portal do
                        cliente.
                    </>
                }
                textActionButton="Sim, excluir"
                title="Excluir publicação"
                onAction={() => handleDelete(idToDelete!)}
                onClose={() => setIdToDelete(undefined)}
                isLoading={isSending}
            />
            <ConfirmationModalComponent
                isVisible={isUnpublishModalVisible}
                message={
                    <>
                        Tem certeza que deseja cancelar a publicação da
                        notificação selecionada? A notificação deixará de ser
                        exibida no aplicativo / portal do cliente, podendo ser
                        publicada novamente posteriormente.
                    </>
                }
                textActionButton="Sim, inativar"
                title="Inativar publicação"
                onAction={() => handleUnpublish(selectedNotification!)}
                onClose={() => {
                    setSelectedNotification(undefined)
                    setIsUnpublishModalVisible(false)
                }}
                isLoading={isSending}
            />
        </NotificationTabContainer>
    )
}

const LoadingContainer = styled.div`
    display: flex;
    align-self: center;
    align-items: center;
    height: 100%;
`

const TabContentContainer = styled.div``

const TabHeaderContainer = styled.div`
    display: flex;
    width: 99%;
    justify-content: space-between;
`

const FilterContainer = styled.div`
    display: flex;
    width: 30%;
    padding: 25px 0 8px 22px;
`

const NewNotificationButtonContainer = styled.div`
    padding: 45px 20px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 15px;
`
const CardsContainer = styled.div``

const NotificationTabContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: absolute;
    height: calc(100% - 260px);
    width: calc(100% - 64px);
    bottom: 28px;
    left: 32px;
    background-color: ${COLORS.primaryWhite};
    border: 1px solid ${COLORS.borderGray};
    overflow: auto;
`

export default NotificationContentScreen
